import { useAtom } from 'jotai'
import { Suspense, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useFiles } from 'api/queries/useFiles'
import NotFoundIcon from 'assets/icons/notfound.svg'
import { awardsFilesAtom } from 'atoms/awardsFiles.atom'
import { filtersAtom } from 'atoms/filters.atom'
import { ModalAtom } from 'atoms/modals.atom'
import { AwardModal } from 'components/awardModal/awardModal'
import { Spinner } from 'components/uikit/Spinner'
import { AllCategories } from 'constants/awards.constants'
import { AwardsCategorySlider } from 'containers/AwardsCategorySlider'
import { DefaultLayout } from 'layouts/DefaultLayout'
import { AnalyticsActionType } from 'types/analytics.type'
import { AwardsFile } from 'types/AwardsFile'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

export const HomePage = () => {
  const location = useLocation()
  const [awardsFiles, setAwardFiles] = useAtom(awardsFilesAtom)

  const [modalOpen] = useAtom(ModalAtom)
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const [filters, setFilters] = useAtom(filtersAtom)

  // Fetch data for all years
  const { data: files2023, isLoading: isLoading2023 } = useFiles(2023)({})
  const { data: files2024, isLoading: isLoading2024 } = useFiles(2024)({})
  const { data: files2025, isLoading: isLoading2025 } = useFiles(2025)({})

  const isLoading = isLoading2023 || isLoading2024 || isLoading2025

  useEffect(() => {
    if (location.search !== '' && filters.category) {
      const search = decodeURI(location.search.split('&')[0].split('category=')[1])
      setFilters({ ...filters, category: [search] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (modalOpen.isOpen) {
      const body = document.querySelector('body')
      if (body) {
        body.style.overflowY = 'hidden'
      }
    }
  }, [modalOpen.isOpen])

  const filteredFiles = useMemo(() => {
    if (!files2023 && !files2024 && !files2025) return []

    // Combine all files from selected years
    const files = [...(files2023 || []), ...(files2024 || []), ...(files2025 || [])]

    // Convert the object to an array
    const filesArray = Object.values(files)
    const searchTerms = filters.search.toLowerCase().split(' ')

    return filesArray.filter((file: AwardsFile) => {
      return (
        file.name &&
        searchTerms.every(term => file.name.toLowerCase().includes(term)) &&
        (filters.ou.length > 0 && filters.ou.length > 0
          ? filters.ou.some(ou =>
              file.ou
                .toLowerCase()
                .split(',')
                .map(o => o.trim())
                .includes(ou.toLowerCase()),
            )
          : true) &&
        (filters.year.length > 0 ? filters.year.some(year => parseInt(year) === file.year) : true) &&
        (filters.category.length > 0
          ? filters.category.map(cat => cat.toLowerCase()).includes(file.category.toLowerCase())
          : true) &&
        (filters.entry === 'all'
          ? true
          : filters.entry === 'nominee'
            ? !file.winner
            : filters.entry === 'winner'
              ? file.winner
              : file.braveBoundless)
      )
    })
  }, [files2023, files2024, files2025, filters])

  useEffect(() => {
    trackAnalytics({
      type: AnalyticsActionType.page,
      payload: ANALYTICS_EVENTS.PAGEVIEW.PAGE_VIEW_MAIN,
    })
  }, [])
  useEffect(() => {
    setAwardFiles(filteredFiles)
  }, [filteredFiles, setAwardFiles])

  return (
    <Suspense fallback={null}>
      <DefaultLayout>
        {isLoading ? (
          <Spinner
            className="w-full h-[40px] mt-6 flex items-center justify-center bg-[#F4F4F4]"
            size="m"
            color="black"
          />
        ) : (
          AllCategories.map((category: string) => {
            const formattedCategory = category.toLowerCase().replace(/ /g, '_')
            return (
              <div ref={el => (categoryRefs.current[formattedCategory] = el)} key={category}>
                <AwardsCategorySlider title={category} category={category} awards={awardsFiles} />
              </div>
            )
          })
        )}
        {!isLoading && awardsFiles.length === 0 && (
          <div className="text-center w-full flex flex-col items-center">
            <img src={NotFoundIcon} alt="Not found" className="w-[80px] h-[80px] mb-6" />
            <p className="font-TCCC text-[20px] leading-7 font-medium mb-3">No results for this search</p>
            <p className="font-TCCCtext max-w-[392px] text-center text-[14px] leading-[22px] font-normal">
              There are no results for this search, try a another one or use different filters.
            </p>
          </div>
        )}
        <AwardModal />
      </DefaultLayout>
    </Suspense>
  )
}
